import { memo, useEffect, useState, useCallback } from "react";
import AddItemButton from "js/Component/AddItemButton";
import { deleteForm, fetchAllForms, duplicateForm } from "js/Api/forms";
import toast from "react-hot-toast";
import DeleteModal from "js/Component/DeleteModal";
import DataTable from "../common/DataTable";
import FormModal from "../common/FormModal";
import moment from 'moment';

const FormManagement = (props) => {
    const { org, onRowClick, onFormCreated, setActiveTab, setEditTemplateItem } = props;

    // State management
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDuplicating, setIsDuplicating] = useState(false);

    // Modal states
    const [isFormModalVisible, setIsFormModalVisible] = useState(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [editableItem, setEditableItem] = useState(null);
    const [itemToDelete, setItemToDelete] = useState(null);

    const tableConfig = {
        columns: [
            {
                key: "formName",
                label: "Name",
                render: (item) => item?.formName || "-",
            },
            {
                key: "formType",
                label: "Form Type",
                render: (item) => {
                    const typeMap = {
                        general: "General",
                        checklist: "Checklist",
                        "single-question": "Single-question",
                    };
                    return typeMap[item?.formType] || "General";
                },
            },
            // {
            //     key: "displayType",
            //     label: "Display Type",
            //     render: (item) => {
            //         const typeMap = {
            //             normal: "Normal",
            //             "fullscreen-dialog": "Fullscreen dialog",
            //         };
            //         return typeMap[item?.displayType] || "Normal";
            //     },
            // },
            {
                key: "startTime",
                label: "Scheduled on",
                render: (item) => {
                    return item?.startTime ? moment(item?.startTime).format('DD/MM/YYYY hh:mm a') : "";
                },
            },
            {
                key: "recipients", label: "Recipients",
                render: (item) => {
                    return item?.sentTo === "device" ? item.recipients.toString() + (item.recipients === 1 ? " device" : " devices") : item?.sentTo === "policy_group" ? item.recipients.toString() + (item.recipients === 1 ? "  policy group" : "  policy groups") : "Everyone";
                },
            },
            {
                key: "responses",
                label: "Number of Responses",
                render: (item) => item?.responses || 0,
            },
            {
                key: "formStatus",
                label: "Status",
                render: (item) => item?.formStatus === 'published' ? 'Published' : 'Draft',
            },
        ],
    };

    // Data fetching with filtering for forms (templateType === null)
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await fetchAllForms(org?.id);
            if (response?.success) {
                const allData = Array.isArray(response?.data) ? response?.data : [];

                const formsData = allData.filter((item) => {
                    const t = item.templateType;
                    return t == null;
                }).sort((a, b) => {

                    if (a.startTime === null || a.startTime === undefined) {
                        return -1; // Place 'a' (with null/undefined date) before 'b'
                    }
                    if (b.startTime === null || b.startTime === undefined) {
                        return 1; // Place 'b' (with null/undefined date) after 'a'
                    }
                    return new Date(b.startTime).getTime() - new Date(a.startTime).getTime();
                });

                setItems(formsData);
            }
        } catch (error) {
            toast.error("Failed to fetch forms");
            console.error("Error fetching forms:", error);
        } finally {
            setIsLoading(false);
        }
    }, [org?.id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Event handlers
    const handleAdd = () => {
        setEditableItem(null);
        setIsFormModalVisible(true);
    };

    const handleEdit = (item) => {
        setEditableItem(item);
        setIsFormModalVisible(true);
    };

    const handleDuplicate = async (item) => {
        if (!item?.id) {
            toast.error("Invalid form ID");
            return;
        }

        setIsDuplicating(true);
        try {
            const response = await duplicateForm(item.id, false);
            if (response?.success) {
                toast.success("Form duplicated successfully");
                fetchData();
            } else {
                toast.error(response?.error || "Failed to duplicate form");
            }
        } catch (error) {
            toast.error("Failed to duplicate form");
            console.error("Error duplicating form:", error);
        } finally {
            setIsDuplicating(false);
        }
    };

    const handleDelete = (item) => {
        setItemToDelete(item);
        setIsDeleteModalVisible(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete?.id) return;

        try {
            const response = await deleteForm(itemToDelete.id);
            if (response?.success) {
                toast.success("Form deleted successfully");
                fetchData();
                setIsDeleteModalVisible(false);
                setItemToDelete(null);
            } else {
                toast.error(response?.error || "Failed to delete form");
            }
        } catch (error) {
            toast.error("Failed to delete form");
            console.error("Error deleting form:", error);
        }
    };

    const handleModalClose = () => {
        setIsFormModalVisible(false);
        setEditableItem(null);
    };

    const handleFormSaved = (savedForm, isNewForm) => {
        fetchData();
        handleModalClose();
        if (isNewForm && savedForm && onFormCreated) {
            onFormCreated(savedForm);
        }
    };

    const OnCopyAsTemplate = async (item) => {
        setIsDuplicating(true);
        try {
            const response = await duplicateForm(item.id, true);
            if (response?.success) {
                toast.success("Form duplicated as Template successfully");
                setEditTemplateItem(response.data.id)
                setActiveTab("templates");
            } else {
                toast.error(response?.error || "Failed to duplicate form");
            }
        } catch (error) {
            toast.error("Failed to duplicate form");
            console.error("Error duplicating form:", error);
        } finally {
            setIsDuplicating(false);
        }
    }

    return (
        <>
            <AddItemButton buttonText="New Form" onClick={handleAdd} />
            <div className="clear"></div>
            <br />

            <DataTable
                data={items}
                columns={tableConfig.columns}
                isLoading={isLoading}
                isDuplicating={isDuplicating}
                onRowClick={onRowClick}
                onEdit={handleEdit}
                onDuplicate={handleDuplicate}
                onDelete={handleDelete}
                isTemplate={false}
                CopyAsTemplate={OnCopyAsTemplate}
            />

            <FormModal
                visible={isFormModalVisible}
                editableItem={editableItem}
                org={org}
                onClose={handleModalClose}
                onSaved={handleFormSaved}
                isTemplate={false}
            />

            <DeleteModal
                visible={isDeleteModalVisible}
                onClose={() => setIsDeleteModalVisible(false)}
                onCancel={() => setIsDeleteModalVisible(false)}
                onDelete={confirmDelete}
                title="Are you sure you want to delete this form?"
            />
        </>
    );
};

export default memo(FormManagement);
